import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  signal,
} from '@angular/core';
import {
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  addToCurrentBasket,
  createNewBasket,
} from '@epicuro-next/constants/features/localization';
import { selectActiveAccount } from '@epicuro-next/state/accounts-state';
import {
  selectBasketUploadRegardlessPresetRule,
  selectBasketUploadSkuLimitRule,
} from '@epicuro-next/state/basket-state';
import {
  selectIsUploadingSDO,
  uploadSDOBasket,
  uploadSDOBasketSuccess,
} from '@epicuro-next/state/basket-upload-state';
import { isNonNullish } from '@epicuro-next/utilities';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Actions, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { PulseOrderType } from 'models-common';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, map, take, takeUntil } from 'rxjs/operators';

import { UploadBasketOptions } from '../../models/upload-basket-options.model';
import { parseItems } from '../../shared/parse-items';
import { quantityValidator } from '../../shared/quantity-validator';

interface UploadOption {
  value: UploadBasketOptions;
  e2e: string;
  displayValue: string;
}

export const uploadBasketOptions: UploadOption[] = [
  {
    value: UploadBasketOptions.Now,
    e2e: 'cbasket-upload-now',
    displayValue:
      $localize`:uploadBasket|upload basket now option@@uploadBasketNowOption:Now`.toString(),
  },
  {
    value: UploadBasketOptions.Future,
    e2e: 'cbasket-upload-future',
    displayValue:
      $localize`:uploadBasket|upload basket future option@@uploadBasketFutureOption:Future`.toString(),
  },
  {
    value: UploadBasketOptions.IsConsignment,
    e2e: 'cbasket-upload-consignment',
    displayValue:
      $localize`:uploadBasket|upload basket consignment option@@uploadBasketConsignmentOption:Bulk`.toString(),
  },
];

@Component({
  selector: 'epc-upload-basket-modal',
  templateUrl: './upload-basket-modal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class UploadBasketModalComponent implements OnDestroy {
  private destroy$ = new Subject<void>();

  public readonly basketUploadButtons = [
    {
      btnText: createNewBasket,
      addToExistingBasket: false,
    },
    {
      btnText: addToCurrentBasket,
      addToExistingBasket: true,
    },
  ];

  public addToExistingBasketUploadMode = signal(true);

  public submitted$ = new BehaviorSubject<boolean>(false);
  public isUploading = this.store.selectSignal(selectIsUploadingSDO);
  public skuLimit$ = this.store.pipe(select(selectBasketUploadSkuLimitRule));
  public basketUploadRegardlessPreset = this.store.selectSignal(
    selectBasketUploadRegardlessPresetRule,
  );

  public uploadBasketOptions$: Observable<UploadOption[]> = this.store.pipe(
    select(selectActiveAccount),
    filter(isNonNullish),
    take(1),
    map(({ authorizedForFutureOrder, authorizedForConsignmentOrder }) => {
      if (authorizedForFutureOrder) {
        if (authorizedForConsignmentOrder) {
          return uploadBasketOptions;
        }

        return uploadBasketOptions.filter(
          (o) => o.value !== UploadBasketOptions.IsConsignment,
        );
      }

      return [];
    }),
  );

  public uploadForm = new UntypedFormGroup({
    products: new UntypedFormControl(
      '',
      [Validators.required, quantityValidator],
      (control) => {
        return this.skuLimit$.pipe(
          take(1),
          map((limit: number) => {
            const skuQuantity = parseItems(control.value).length;
            return skuQuantity > limit
              ? {
                  maxSKULimit: skuQuantity,
                }
              : null;
          }),
        );
      },
    ),
    uploadOption: new UntypedFormControl(UploadBasketOptions.Now),
  });

  public get productsControl() {
    return this.uploadForm.get('products') as UntypedFormControl;
  }

  public get uploadOptionControl() {
    return this.uploadForm.get('uploadOption') as UntypedFormControl;
  }

  constructor(
    private store: Store,
    private modal: NgbActiveModal,
    private actions$: Actions,
  ) {}

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public confirmUpload(addToExistingBasket: boolean) {
    this.submitted$.next(true);

    if (this.uploadForm.invalid) {
      return;
    }

    this.addToExistingBasketUploadMode.set(addToExistingBasket);

    this.store.dispatch(
      uploadSDOBasket({
        uploadBasket: {
          isConsignment:
            this.uploadOptionControl.value ===
            UploadBasketOptions.IsConsignment,
          items: parseItems(this.productsControl.value),
          orderType: this.getOrderType(),
        },
        addToExistingBasket,
      }),
    );

    this.actions$
      .pipe(ofType(uploadSDOBasketSuccess), take(1), takeUntil(this.destroy$))
      .subscribe(() => this.close());
  }

  public close() {
    this.modal.close();
  }

  private getOrderType(): PulseOrderType {
    if (this.basketUploadRegardlessPreset()) {
      return PulseOrderType.Any;
    }

    return [
      UploadBasketOptions.IsConsignment,
      UploadBasketOptions.Future,
    ].includes(this.uploadOptionControl.value)
      ? PulseOrderType.Future
      : PulseOrderType.Now;
  }
}
